import React, { useContext } from "react"
import styled from "styled-components"
import { useStaticQuery, graphql } from "gatsby"
import Grid from "../shared/grid"
import Breakpoints from "../shared/breakpoints"
import { T, FontFamilyRegular } from "../shared/typography.styles"
import { UIContext } from "../shared/layout"
import Img from "gatsby-image"

const Container = styled.div`
  padding: 48px 0;
  border-bottom: 1px solid #e0e0e0;

  @media (min-width: ${Breakpoints.md}) {
    padding: 64px 0 48px;
  }
  @media (min-width: ${Breakpoints.lg}) {
    padding: 128px 0 74px;
  }
`

const Title = styled.h2`
  ${T.sm.h2.bold}
  text-align: center;
  grid-column: 1 / -1;
  color: var(--slate);
  @media (min-width: ${Breakpoints.md}) {
    ${T.md.h2.bold}
    grid-column: 2 / -2;
  }
  @media (min-width: ${Breakpoints.lg}) {
    ${T.lg.h2.bold}
    text-align: left;
    grid-column: 2 / 7;
  }
`
const Description = styled.h2`
  ${T.sm.body.regular}
  text-align: center;
  grid-column: 1 / -1;
  margin: 24px 0 0;
  color: var(--body-text-grey);
  @media (min-width: ${Breakpoints.md}) {
    ${T.md.body.regular}
    grid-column: 2 / -2;
  }
  @media (min-width: ${Breakpoints.lg}) {
    ${T.lg.body.regular}
    text-align: left;
    grid-column: 7 / -2;
    margin: 0;
  }
`

const ImageContainer = styled.div`
  margin: 48px 0 0;
  display: flex;
  flex-direction: column;
  grid-column: 1 / -1;
  @media (min-width: ${Breakpoints.lg}) {
    margin: 64px 0 0;
  }
`

const UseCaseAside = styled.aside`
  ${FontFamilyRegular}

  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 140%;
  /* identical to box height, or 17px */

  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--body-text-grey);

  padding: 12px 16px;
  border: 1px solid var(--slate);
  grid-column: 1 / -1;
  justify-self: center;

  margin-top: 32px;

  ::before {
    content: "+";
    color: var(--burnt-orange);
    margin-right: 8px;
  }

  @media (min-width: ${Breakpoints.md}) {
    margin-top: 24px;
    justify-self: right;
  }

  @media (min-width: ${Breakpoints.lg}) {
    grid-column: 1 / -2;
    font-size: 16px;
  }
`

const diagramAlts = [
  "Stadiums: Seat Assignment, Parking Vacancy, Venue Wayfinding, Proximity Marketing, Multi-modal Transport, Multi-destination Wayfinding. Transportation: Multi-modal Transport, Multi-destination Wayfinding. REITs & Malls: Leasing Tools, Digital Directory, Web Wayfinding, Paid Search & Ads, Triggered Events & Promotions.",
  "Parking Lots: Parking Vacancy. Construction Projects: Dynamic Document, Asset Tracking.",
  "Mixed Use Spaces: Leasing Tools, Digital Directory, Web & Mobile Wayfinding, Operational Performance, Parking Availability. Retailers: Pick-and-Pack Optimization, Triggered Events & Promotions, Web & Mobile Wayfinding.",
  "Airports: Parking Vacancy, Digital Directory, Web Wayfinding, Operational Performance. Emergency Services: Fire Response, Evacuation Planning, Asset Tracking, Building Plan Annotation.",
]

export default function Overview(props) {
  const query = useStaticQuery(graphql`
    query {
      useCaseSm1: file(
        relativePath: {
          eq: "3_RESOURCES/Partner Program/03_mobile/sdk_infographic_mobile_1.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 600, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      useCaseSm2: file(
        relativePath: {
          eq: "3_RESOURCES/Partner Program/03_mobile/sdk_infographic_mobile_2.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 600, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      useCaseSm3: file(
        relativePath: {
          eq: "3_RESOURCES/Partner Program/03_mobile/sdk_infographic_mobile_3.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 600, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      useCaseSm4: file(
        relativePath: {
          eq: "3_RESOURCES/Partner Program/03_mobile/sdk_infographic_mobile_4.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 600, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      useCaseMdLg: file(
        relativePath: {
          eq: "3_RESOURCES/Partner Program/01_desktop/usecases_desktop_tablet.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1440, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  const {
    state: { breakpoint },
  } = useContext(UIContext)

  return (
    <Container>
      <Grid>
        <Title>Succeed With Us as a Mappedin Partner</Title>
        <Description>
          Our program is designed to help our partners build, sell, and empower
          their customers to invest in place-based experiences by providing an
          accessible suite of map management and building tools.
        </Description>
        <ImageContainer>
          {breakpoint === "sm" ? (
            <>
              <Img
                fluid={query.useCaseSm1.childImageSharp.fluid}
                alt={`Use Cases - ${diagramAlts[0]}`}
              />
              <Img
                fluid={query.useCaseSm2.childImageSharp.fluid}
                alt={`Use Cases - ${diagramAlts[1]}`}
              />
              <Img
                fluid={query.useCaseSm3.childImageSharp.fluid}
                alt={`Use Cases - ${diagramAlts[2]}`}
              />
              <Img
                fluid={query.useCaseSm4.childImageSharp.fluid}
                alt={`Use Cases - ${diagramAlts[3]}`}
              />
            </>
          ) : (
            <>
              <Img
                fluid={query.useCaseMdLg.childImageSharp.fluid}
                alt={`Use Cases - ${diagramAlts[0]} ${diagramAlts[1]} ${diagramAlts[2]} ${diagramAlts[3]}`}
              />
            </>
          )}
        </ImageContainer>
        <UseCaseAside>Many more use cases</UseCaseAside>
      </Grid>
    </Container>
  )
}
